<template>
    <modal id="salary-payment-modal"
           size="large"
           v-model="showModal"
           :title="$t('payment')"
           @submit="submitData"
           :loading="loading"
           :preloader="preloader"
           :scrollable="false"
    >
        <div class="row">
            <div class="col-md-12">
                <app-form-group
                    type="multi-select"
                    :label="$t('account')"
                    list-value-field="name"
                    v-model="accountIds"
                    :list="accounts"
                    @deletedId="removeAccountId"
                    @addedId="addAccountId"
                    :error-message="$errorMessage(errors, 'accounts')"
                />
            </div>

            <div class="col-md-12" v-if="accountIds.length">
                <div class="row" v-for="(account, index) in selectedAccounts" :key="`payment-${account.id}-${index}`">
                    <div class="col-11 form-group">
                        <div class="row align-items-center">
                            <div class="col-sm-12 col-md-6">
                                <label class="text-left d-block mb-lg-0">
                                    {{ account.name }}
                                </label>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <app-input
                                    type="number"
                                    min="0"
                                    v-model="paymentValues[account.id]"
                                    :placeholder="$placeholder('amount')"
                                    :required="true"
                                />
                                <small v-if="errorsByAccount[account.id]" class="text-danger d-block mt-1">
                                    {{ errorsByAccount[account.id] }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div class="col-1 pl-0 ml-0">
                        <a href="#" @click.prevent="removeSelectedAccount(account.id)">
                            <app-icon name="trash" class="text-primary mt-2" width="22"/>
                        </a>
                    </div>
                </div>
            </div>

            <!-- Summary Section -->
            <div class="col-md-12 mt-3" v-if="accountIds.length">
                <div class="border rounded p-3 bg-light">
                    <div class="row">
                        <div class="col-6 text-right font-weight-bold">{{ $t('net_salary') }}:</div>
                        <div class="col-6 pl-0">{{ formatCurrency(numberFormatter(netSalary)) }}</div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-6 text-right font-weight-bold">{{ $t('total_payment') }}:</div>
                        <div class="col-6 pl-0">{{ formatCurrency(numberFormatter(totalPayment)) }}</div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-6 text-right font-weight-bold">{{ $t('difference') }}:</div>
                        <div class="col-6 pl-0" :class="{'text-danger': paymentDifference !== 0, 'text-success': paymentDifference === 0}">
                            {{ formatCurrency(numberFormatter(paymentDifference)) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {axiosDelete, axiosGet, axiosPatch, axiosPost} from "../../../../common/Helper/AxiosHelper";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {formatCurrency, numberFormatter} from "../../../../common/Helper/Support/SettingsHelper";

export default {
    name  : "SalaryPaymentModal",
    mixins: [ModalMixin, FormHelperMixins],
    props : {
        rowData: {
            type   : Object,
            default: () => ({})
        },
    },
    data() {
        return {
            TENANT_BASE_URL,
            errors          : {},
            errorsByAccount : {},
            accounts        : [],
            accountIds      : [],
            paymentValues   : {},
            payments        : [], // existing payments from server
            removedIds      : [], // ids to delete
        }
    },
    created() {
        this.fetchAccounts();
        this.fetchPayments();
    },
    methods: {
        fetchPayments() {
            this.preloader = true;
            axiosGet(`${TENANT_BASE_URL}app/salary-payment?salary_id=${this.rowData.id}&per_page=0`)
                .then(({data}) => {
                    const items = data.data ?? data;
                    this.payments = items.map(item => ({
                        id                : item.id,
                        salary_id         : item.salary_id,
                        finance_account_id: item.finance_account_id,
                        account_name      : item.account_name,
                        amount            : Number(item.amount),
                    }));
                    this.accountIds = this.payments
                        .filter(p => p.finance_account_id)
                        .map(p => p.finance_account_id);
                    this.payments.forEach(payment => {
                        this.$set(this.paymentValues, payment.finance_account_id, payment.amount);
                    });
                })
                .catch(error => {
                    this.$toastr.e(error.response?.data?.message || error.message);
                })
                .finally(() => this.preloader = false);
        },
        fetchAccounts() {
            axiosGet(`${TENANT_BASE_URL}app/finance-accounts?all=1`)
                .then(({data}) => {
                    this.accounts = data;
                })
                .catch(error => this.$toastr.e(error.response?.data?.message || error.message));
        },
        addAccountId(id) {
            if (!this.accountIds.includes(id)) {
                this.accountIds.push(id);
            }
            if (!this.paymentValues[id]) {
                this.$set(this.paymentValues, id, 0);
            }
        },
        removeAccountId(id) {
            const index = this.accountIds.findIndex(item => item === id);
            if (index > -1) {
                this.accountIds.splice(index, 1);
            }
            this.$delete(this.paymentValues, id);
            const existing = this.payments.find(p => p.finance_account_id === id);
            if (existing?.id) {
                this.removedIds.push(existing.id);
            }
        },
        removeSelectedAccount(id) {
            this.removeAccountId(id);
        },
        formatCurrency,
        numberFormatter,
        submitData() {
            this.errors = {};
            this.errorsByAccount = {};

            // Frontend validation: total payment must equal net salary
            if (this.paymentDifference !== 0) {
                this.$toastr.e(this.$t('payment_must_equal_net_salary'));
                return;
            }

            this.loading = true;
            const payload = {
                salary_id: this.rowData.id,
                payments : [],
            };

            const paymentIdByAccount = {};
            this.payments.forEach(p => paymentIdByAccount[p.finance_account_id] = p.id);

            this.accountIds.forEach(accountId => {
                payload.payments.push({
                    id                : paymentIdByAccount[accountId] || null,
                    finance_account_id: accountId,
                    account_name      : this.accounts.find(acc => acc.id === accountId)?.name,
                    amount            : this.paymentValues[accountId] ?? 0,
                });
            });

            this.removedIds.forEach(id => {
                payload.payments.push({
                    id      : id,
                    _delete : true,
                    salary_id: this.rowData.id,
                });
            });

            axiosPost(`${TENANT_BASE_URL}app/salary-payment/sync`, payload)
                .then(() => {
                    this.toastAndReload(this.$t('updated'), 'salary-sheet-table');
                    this.closeModal();
                })
                .catch(error => {
                    this.errors = error.response?.data?.errors || {};
                    this.errorsByAccount = {};

                    if (this.errors) {
                        Object.keys(this.errors).forEach(key => {
                            const match = key.match(/payments\.(\d+)\.amount/);
                            if (match) {
                                const index = Number(match[1]);
                                const item  = payload.payments[index];
                                if (item?.finance_account_id) {
                                    this.$set(this.errorsByAccount, item.finance_account_id, this.errors[key][0]);
                                }
                            }
                        });
                    }

                    this.$toastr.e(error.response?.data?.message || error.message);
                })
                .finally(() => this.loading = false);
        },
        closeModal() {
            this.$emit('input', false);
            $('#salary-payment-modal').modal('hide');
        }
    },
    computed: {
        selectedAccounts() {
            return this.accountIds
                .map(id => this.accounts.find(acc => acc.id === id))
                .filter(Boolean);
        },
        netSalary() {
            return Number(this.rowData.net_salary) || 0;
        },
        totalPayment() {
            return this.accountIds.reduce((sum, accountId) => {
                return sum + (Number(this.paymentValues[accountId]) || 0);
            }, 0);
        },
        paymentDifference() {
            return this.totalPayment - this.netSalary;
        }
    }
}
</script>
